/**
 * QuoteModal.js
 * 제품 상세 정보 및 견적/장바구니 기능을 제공하는 모달 컴포넌트
 * 
 * 주요 기능:
 * - 제품 상세 정보 표시 (이미지, 가격, 옵션 등)
 * - 수량 조절
 * - 옵션 선택
 * - 장바구니 담기
 * - 견적 의뢰
 * - 반응형 레이아웃 (모바일/데스크톱)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Plus, Minus, ShoppingCart, Calculator, ChevronLeft, ChevronRight, Eye, Search } from 'lucide-react';
import { CiImageOff } from 'react-icons/ci';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { commonAPI } from '../../services/api';
import './QuoteModal.css';

import ImageWithFallback from '../common/ImageWithFallback';
import ImageModal from '../common/ImageModal';
import Modal from '../common/Modal';
import ProductQuoteModal from './ProductQuoteModal';
import { toast } from 'react-toastify';
import { addToCart } from '../../utils/cartUtils';
import useScrollLock from '../../hooks/useScrollLock';

/**
 * 모바일 환경 감지 커스텀 훅
 * 화면 크기가 768px 이하일 경우 모바일로 판단
 * 
 * @returns {boolean} 모바일 여부
 */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

/**
 * QuoteModal 컴포넌트
 * 
 * @param {Object} product - 표시할 제품 정보
 * @param {boolean} isOpen - 모달 열림/닫힘 상태
 * @param {Function} onClose - 모달 닫기 콜백 함수
 */
const QuoteModal = ({ product, isOpen, onClose }) => {
  useScrollLock(isOpen);
  const isMobile = useIsMobile(); // 모바일 감지

  // 상태 관리
  const [quantity, setQuantity] = useState(1); // 선택 수량
  const [showLoginModal, setShowLoginModal] = useState(false); // 로그인 모달 표시 여부
  const [showCartSuccessModal, setShowCartSuccessModal] = useState(false); // 장바구니 추가 성공 모달
  const [optionValues, setOptionValues] = useState([]); // 옵션 목록
  const [selectedOptionValue, setSelectedOptionValue] = useState(''); // 선택된 옵션
  const [loadingOptions, setLoadingOptions] = useState(false); // 옵션 로딩 상태
  const [showQuoteRequestModal, setShowQuoteRequestModal] = useState(false); // 견적의뢰 모달 표시 여부

  // 이미지 갤러리 상태
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // 현재 선택된 이미지 인덱스
  const [images, setImages] = useState([]); // 이미지 목록
  const [isImageModalOpen, setIsImageModalOpen] = useState(false); // 이미지 확대 모달

  // 전역 상태 및 네비게이션
  const { globalState } = useAuth();
  const navigate = useNavigate();

  // 중복 로딩 방지를 위한 ref
  const loadedOptCdRef = useRef(null); // 이전에 로드한 옵션 코드
  const isLoadingRef = useRef(false); // API 호출 중복 방지 플래그

  /**
   * 옵션 코드 유효성 검사
   * optCd가 없거나 'OP0000'일 경우 false 반환
   */
  const isValidOptionCode = (optCd) => {
    // optCd가 없거나, 빈 문자열이거나, 'OP0000'이면 false
    if (!optCd || optCd.trim() === '' || optCd === 'OP0000') {
      return false;
    }
    return true;
  };

  /**
   * 옵션값 로드 함수
   * 중복 API 호출을 방지하고 제품의 옵션 목록을 가져옴
   * 
   * @param {string} optCd - 옵션 코드
   */
  const loadOptionValues = useCallback(async (optCd) => {
    // optCd가 유효하지 않거나, 이미 로딩 중이거나 같은 optCd면 중복 호출 방지
    if (!isValidOptionCode(optCd) || isLoadingRef.current || loadedOptCdRef.current === optCd) {
      return;
    }

    isLoadingRef.current = true;

    try {
      setLoadingOptions(true);
      const options = await commonAPI.getOptionValues(optCd);

      if (options && Array.isArray(options)) {
        setOptionValues(options);
        // 첫 번째 옵션을 기본값으로 선택
        if (options.length > 0) {
          setSelectedOptionValue(options[0].optValCd);
        }
      } else {
        setOptionValues([]);
        setSelectedOptionValue('');
      }

      // 성공적으로 로드된 optCd 저장
      loadedOptCdRef.current = optCd;

    } catch (error) {
      console.error('옵션값 로드 실패:', error);
      setOptionValues([]);
      setSelectedOptionValue('');
    } finally {
      setLoadingOptions(false);
      isLoadingRef.current = false;
    }
  }, []);

  /**
   * 모달이 열릴 때마다 상태 초기화 및 옵션 로드
   */
  useEffect(() => {
    if (isOpen && product) {
      // 기본 상태 초기화
      setQuantity(1);
      setShowLoginModal(false);
      setSelectedOptionValue('');
      setLoadingOptions(false);
      setCurrentImageIndex(0);

      // 이미지 목록 초기화
      if (product.fileData && Array.isArray(product.fileData) && product.fileData.length > 0) {
        // fileData가 있으면 fileData 사용
        const imageList = product.fileData.map(file => file.filePath).filter(Boolean);
        setImages(imageList);
      } else if (product.FILEPATH && product.FILEPATH !== 'no_image.png' && product.FILEPATH !== '') {
        // fileData가 없고 FILEPATH가 유효한 이미지면 단일 이미지
        setImages([product.FILEPATH]);
      } else {
        // 이미지가 없음
        setImages([]);
      }

      // 옵션 처리
      const currentOptCd = product.optCd || null;

      console.log('QuoteModal - optCd:', currentOptCd, 'isValid:', isValidOptionCode(currentOptCd));

      if (isValidOptionCode(currentOptCd) && loadedOptCdRef.current !== currentOptCd) {
        // 새로운 옵션 코드 - 로드 필요
        setOptionValues([]);
        loadOptionValues(currentOptCd);
      } else {
        // 옵션이 없거나 유효하지 않은 상품
        setOptionValues([]);
        setSelectedOptionValue('');
        setLoadingOptions(false);
        loadedOptCdRef.current = null;
      }

      // ESC 키로 모달 닫기 이벤트
      const handleEscKey = (e) => {
        if (e.key === 'Escape') {
          handleClose();
        }
      };

      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }

    // 모달이 닫히면 초기화
    if (!isOpen) {
      loadedOptCdRef.current = null;
      isLoadingRef.current = false;
      setLoadingOptions(false);
      setCurrentImageIndex(0);
      setImages([]);
    }
  }, [isOpen, product?.itemCd, loadOptionValues]);

  /**
   * 이전 이미지로 이동
   */
  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  /**
   * 다음 이미지로 이동
   */
  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  /**
   * 썸네일 클릭 핸들러
   */
  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
  };

  /**
   * 이미지 클릭 핸들러 (확대 보기)
   * 이미지 배열과 현재 인덱스를 ImageModal에 전달
   */
  const handleImageClick = () => {
    if (images.length > 0) {
      setIsImageModalOpen(true);
    }
  };

  // 로그인 상태 확인
  const isLoggedIn = !!globalState.G_USER_ID;

  // 모달이 열려있지 않으면 렌더링하지 않음
  if (!isOpen || !product) {
    return null;
  }

  /**
   * 수량 변경 핸들러
   * 최소 수량은 1로 제한
   * 
   * @param {number} delta - 변경할 수량 (양수: 증가, 음수: 감소)
   */
  const handleQuantityChange = (delta) => {
    const newQuantity = Math.max(1, quantity + delta);
    setQuantity(newQuantity);
  };

  /**
   * 총 금액 계산
   * 할인가가 있으면 할인가 사용, 없으면 판매가 사용
   * 
   * @returns {string} 천단위 구분된 총 금액 문자열
   */
  const calculateTotal = () => {
    const price = product.disPrice || product.salePrice || 0;
    return (price * quantity).toLocaleString();
  };

  /**
   * 견적의뢰 버튼 클릭 핸들러
   * 로그인 및 옵션 선택 여부를 확인한 후 견적의뢰 모달 열기
   */
  const handleQuoteRequest = () => {
    // 로그인 체크
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    // 유효한 옵션 코드가 있고 옵션값 선택 체크
    if (isValidOptionCode(product.optCd) && optionValues.length > 0 && !selectedOptionValue) {
      toast.error('옵션을 선택해주세요.');
      return;
    }

    // 견적의뢰 모달 열기
    setShowQuoteRequestModal(true);
  };

  /**
   * 장바구니 담기 핸들러
   * 거래처별 장바구니 저장
   */
  const handleAddToCart = () => {
    // 로그인 체크
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    // 유효한 옵션 코드가 있고 옵션값 선택 체크
    if (isValidOptionCode(product.optCd) && optionValues.length > 0 && !selectedOptionValue) {
      toast.error('옵션을 선택해주세요.');
      return;
    }

    try {
      const price = product.disPrice || product.salePrice || 0;
      const selectedOption = optionValues.find(opt => opt.optValCd === selectedOptionValue);
      const custCd = globalState.G_CUST_CD || '';

      // cartUtils를 사용하여 거래처별 장바구니에 추가
      const success = addToCart(custCd, {
        itemCd: product.itemCd,
        itemNm: product.itemNm,
        unitNm: product.unitNm,
        optCd: product.optCd || '',
        optValCd: selectedOptionValue || '',
        optValNm: selectedOption ? selectedOption.optValNm : '',
        price: price,
        outUnitPrice: price,
        quantity: quantity,
        filePath: product.FILEPATH,
        totalAmount: price * quantity,
        source: product.isSurplus ? 'surplus' : (product.isEvent ? 'event' : 'general'),
        shipAvDate: product.shipAvDate,
        compNm: product.compNm
      });

      if (success) {
        setShowCartSuccessModal(true);
      } else {
        toast.error('장바구니 추가에 실패했습니다.');
      }

    } catch (error) {
      console.error('장바구니 추가 오류:', error);
      toast.error('장바구니 추가 중 오류가 발생했습니다.');
    }
  };

  /**
   * 모달 닫기 핸들러
   */
  const handleClose = () => {
    onClose();
  };

  /**
   * 출하 가능일 포맷팅
   * YYYYMMDD 형식을 MM.DD (요일) 형식으로 변환
   * 
   * @param {string} dateString - YYYYMMDD 형식의 날짜 문자열
   * @returns {string} 포맷된 날짜 문자열 (예: "03.15 (금)")
   */
  const formatShipDate = (dateString) => {
    if (!dateString) return '';

    // YYYYMMDD 형식을 MM.DD (요일) 형식으로 변환
    if (dateString.length === 8) {
      const year = parseInt(dateString.substr(0, 4));
      const month = parseInt(dateString.substr(4, 2));
      const day = parseInt(dateString.substr(6, 2));

      // Date 객체 생성
      const date = new Date(year, month - 1, day);

      // 요일 배열 (한국어)
      const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
      const weekday = weekdays[date.getDay()];

      // MM.DD (요일) 형식으로 반환
      const formattedMonth = month.toString().padStart(2, '0');
      const formattedDay = day.toString().padStart(2, '0');

      return `${formattedMonth}.${formattedDay} (${weekday})`;
    }

    return dateString;
  };

  /**
   * 백드롭 클릭 핸들러
   * 모달 외부(오버레이) 클릭 시 모달 닫기
   * 
   * @param {Event} e - 클릭 이벤트
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <>
      <div className="quote-modal-overlay" onClick={handleBackdropClick}>
        <div className="quote-modal-container updated">
          {/* 모달 헤더 */}
          <div className="quote-modal-header">
            <h2>상품 정보</h2>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose(e);
              }}
              className="quote-modal-close-button"
              type="button"
              aria-label="모달 닫기"
            >
              <X size={20} />
            </button>
          </div>

          {/* 모달 콘텐츠 - 반응형 레이아웃 */}
          {isMobile ? (
            // 모바일 레이아웃: 상단(이미지+기본정보) + 하단(옵션/수량/총금액)
            <div className="quote-modal-scrollable-content">
              {/* 상단 영역: 이미지(왼쪽) + 기본정보(오른쪽) */}
              <div className="quote-modal-top-section">
                {/* 이미지 섹션 */}
                <div className="quote-modal-image-section">
                  <div className="qm-gallery-wrapper">
                    <div className="qm-gallery-main-container">
                      {images.length > 0 ? (
                        <>
                          {/* 메인 이미지 */}
                          <ImageWithFallback
                            src={images[currentImageIndex]}
                            alt={product.itemNm}
                            className="quote-modal-product-image"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          />

                          {/* 확대보기 오버레이 */}
                          <div className="qm-gallery-image-overlay">
                            <button
                              className="qm-gallery-zoom-btn"
                              onClick={handleImageClick}
                            >
                              <Eye size={16} />
                              확대보기
                            </button>
                          </div>

                          {/* 좌우 화살표 버튼 - 이미지가 2개 이상일 때만 표시 */}

                        </>
                      ) : (
                        <div className="quote-modal-no-image">
                          <CiImageOff size={48} color="#ccc" />
                          <span>이미지 없음</span>
                        </div>
                      )}
                    </div>

                    {/* 썸네일 목록 - 이미지가 2개 이상일 때만 표시 */}
                    {images.length > 1 && (
                      <div className="qm-gallery-thumbnail-wrapper">
                        {images.map((image, index) => (
                          <div
                            key={index}
                            className={`qm-gallery-thumbnail-item ${index === currentImageIndex ? 'qm-gallery-thumbnail-active' : ''
                              }`}
                            onClick={() => handleThumbnailClick(index)}
                          >
                            <ImageWithFallback
                              src={image}
                              alt={`${product.itemNm} ${index + 1}`}
                              className="qm-gallery-thumbnail-img"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* 기본 정보 섹션 */}
                <div className="quote-modal-basic-info-section">
                  {product.itemCd && (
                    <div className="quote-modal-product-code">{product.itemCd}</div>
                  )}

                  <h3 className="quote-modal-product-name">{product.itemNm}</h3>

                  {/* 기본 정보 */}
                  <div className="quote-modal-product-basic">
                    {product.compNm && (
                      <div className="quote-modal-product-row">
                        <span className="quote-modal-product-label">회사명:</span>
                        <span className="quote-modal-product-value">{product.compNm}</span>
                      </div>
                    )}

                    {product.stockQty !== undefined && product.stockQty !== null && (
                      <div className="quote-modal-product-row">
                        <span className="quote-modal-product-label">재고:</span>
                        <span className="quote-modal-product-value" style={{ color: '#ef4444', fontWeight: 'bold' }}>
                          {Number(product.stockQty).toLocaleString()}개
                        </span>
                      </div>
                    )}

                    {product.shipAvDate && (
                      <div className="quote-modal-product-row quote-modal-delivery-row">
                        <span className="quote-modal-delivery-badge">🚚 {formatShipDate(product.shipAvDate)} 출하가능</span>
                      </div>
                    )}

                    {product.unitNm && (
                      <div className="quote-modal-product-row">
                        <span className="quote-modal-product-label">단위:</span>
                        <span className="quote-modal-product-value">{product.unitNm}</span>
                      </div>
                    )}

                    {(product.disPrice > 0 || product.salePrice > 0) && (
                      <>
                        <div className="quote-modal-product-row">
                          <span className="quote-modal-product-label">가격:</span>
                          <span className="quote-modal-product-value price">
                            {Number(product.disPrice || product.salePrice).toLocaleString()}원
                          </span>
                        </div>

                        {product.disPrice > 0 && product.salePrice > 0 && product.disPrice !== product.salePrice && (
                          <div className="quote-modal-product-row">
                            <span className="quote-modal-product-label">정가:</span>
                            <span className="quote-modal-product-value" style={{ textDecoration: 'line-through', color: '#999' }}>{Number(product.salePrice).toLocaleString()}원</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* 하단 영역: 옵션/수량/총금액 */}
              <div className="quote-modal-bottom-section">
                {/* 옵션 선택 - 유효한 옵션 코드일 때만 표시 */}
                {isValidOptionCode(product.optCd) && optionValues.length > 0 && (
                  <div className="quote-modal-option-section">
                    <span className="quote-modal-option-label">옵션:</span>
                    {loadingOptions ? (
                      <div className="quote-modal-option-loading">옵션 로드 중...</div>
                    ) : (
                      <select
                        value={selectedOptionValue}
                        onChange={(e) => setSelectedOptionValue(e.target.value)}
                        className="quote-modal-option-select"
                      >
                        {optionValues.map((option) => (
                          <option key={option.optValCd} value={option.optValCd}>
                            {option.optValNm}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* 수량 선택 */}
                <div className="quote-modal-quantity-section">
                  <label className="quote-modal-quantity-label">수량:</label>
                  <div className="quote-modal-quantity-controls">
                    <button
                      className="quote-modal-quantity-button"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="quote-modal-quantity-input"
                      min="1"
                    />
                    <button
                      className="quote-modal-quantity-button"
                      onClick={() => handleQuantityChange(1)}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* 총 금액 */}
                <div className="quote-modal-total-amount">
                  <span className="quote-modal-total-label">총 금액:</span>
                  <span className="quote-modal-total-price">
                    {calculateTotal()} 원
                  </span>
                </div>

                {/* 모바일 액션 버튼 (내부로 이동) */}
                <div className="quote-modal-actions mobile-actions">
                  <button onClick={handleAddToCart} className="quote-modal-action-button quote-modal-cart-button">
                    <ShoppingCart size={18} />
                    장바구니
                  </button>
                  <button onClick={handleQuoteRequest} className="quote-modal-action-button quote-modal-quote-button">
                    <Calculator size={18} />
                    견적의뢰
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // 데스크톱 레이아웃: 2컬럼 스플릿
            <div className="quote-modal-desktop-layout">
              {/* 이미지 섹션 (왼쪽) */}
              <div className="quote-modal-image-section">
                <div className="qm-gallery-wrapper">
                  <div className="qm-gallery-main-container">
                    {images.length > 0 ? (
                      <>
                        <ImageWithFallback
                          src={images[currentImageIndex]}
                          alt={product.itemNm}
                          className="quote-modal-product-image"
                        />
                        <div className="qm-gallery-image-overlay icon-only">
                          <button
                            className="qm-gallery-zoom-btn icon-btn"
                            onClick={handleImageClick}
                            title="확대보기"
                          >
                            <Search size={24} />
                          </button>
                        </div>

                      </>
                    ) : (
                      <div className="quote-modal-no-image">
                        <CiImageOff size={64} color="#ccc" />
                        <span>이미지 없음</span>
                      </div>
                    )}
                  </div>

                  {images.length > 1 && (
                    <div className="qm-gallery-thumbnail-wrapper">
                      {images.map((image, index) => (
                        <div
                          key={index}
                          className={`qm-gallery-thumbnail-item ${index === currentImageIndex ? 'qm-gallery-thumbnail-active' : ''
                            }`}
                          onClick={() => handleThumbnailClick(index)}
                        >
                          <ImageWithFallback
                            src={image}
                            alt={`${product.itemNm} ${index + 1}`}
                            className="qm-gallery-thumbnail-img"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 정보 섹션 (오른쪽) */}
              <div className="quote-modal-details-section">
                {product.itemCd && (
                  <div className="quote-modal-product-code">{product.itemCd}</div>
                )}

                <h3 className="quote-modal-product-name">{product.itemNm}</h3>

                <div className="quote-modal-product-basic">
                  {/* 회사명/단위 등 메타 정보 테이블 형태로 개선 */}
                  <div className="quote-modal-meta-grid">
                    {product.compNm && (
                      <div className="meta-item">
                        <span className="label">제조사</span>
                        <span className="value">{product.compNm}</span>
                      </div>
                    )}
                    {product.unitNm && (
                      <div className="meta-item">
                        <span className="label">단위</span>
                        <span className="value">{product.unitNm}</span>
                      </div>
                    )}
                    {product.stockQty !== undefined && product.stockQty !== null && (
                      <div className="meta-item">
                        <span className="label">재고</span>
                        <span className="value" style={{ color: '#ef4444', fontWeight: 'bold' }}>
                          {Number(product.stockQty).toLocaleString()}개
                        </span>
                      </div>
                    )}
                  </div>

                  {product.shipAvDate && (
                    <div className="quote-modal-delivery-info">
                      <span className="quote-modal-delivery-badge">{formatShipDate(product.shipAvDate)} 제품등록</span>
                    </div>
                  )}

                  <div className="quote-modal-price-area">
                    {(product.disPrice > 0 || product.salePrice > 0) && (
                      <>
                        <>
                          {/* Original Price (Strikethrough) */}
                          {product.disPrice > 0 && product.salePrice > 0 && product.disPrice !== product.salePrice && (
                            <div className="quote-modal-original-price-row">
                              <span className="original-price-text">{Number(product.salePrice).toLocaleString()}원</span>
                            </div>
                          )}

                          {/* Discount % + Final Price */}
                          <div className="quote-modal-final-price-row">
                            {product.disPrice > 0 && product.salePrice > 0 && product.disPrice !== product.salePrice && (
                              <span className="discount-rate">
                                {Math.round(((product.salePrice - product.disPrice) / product.salePrice) * 100)}%
                              </span>
                            )}
                            <span className="final-price-text">
                              {Number(product.disPrice || product.salePrice).toLocaleString()}원
                            </span>
                          </div>
                        </>
                      </>
                    )}
                  </div>
                </div>

                <div className="quote-modal-form-area">
                  {/* 옵션 선택 */}
                  {isValidOptionCode(product.optCd) && optionValues.length > 0 && (
                    <div className="quote-modal-field-group">
                      <label>옵션 선택</label>
                      {loadingOptions ? (
                        <div className="quote-modal-option-loading">옵션 로드 중...</div>
                      ) : (
                        <select
                          value={selectedOptionValue}
                          onChange={(e) => setSelectedOptionValue(e.target.value)}
                          className="quote-modal-input-select"
                        >
                          {optionValues.map((option) => (
                            <option key={option.optValCd} value={option.optValCd}>
                              {option.optValNm}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* 수량 선택 */}
                  <div className="quote-modal-field-group">
                    <label>수량</label>
                    <div className="quote-modal-quantity-control-modern">
                      <button onClick={() => handleQuantityChange(-1)} disabled={quantity <= 1}><Minus size={16} /></button>
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      />
                      <button onClick={() => handleQuantityChange(1)}><Plus size={16} /></button>
                    </div>
                  </div>

                  {/* 총 금액 */}
                  <div className="quote-modal-total-row">
                    <span className="label">총 주문금액</span>
                    <span className="value">{calculateTotal()} <small>원</small></span>
                  </div>
                </div>

                {/* 데스크톱 액션 버튼 (Right Column) */}
                <div className="quote-modal-actions desktop-actions">
                  <button onClick={handleAddToCart} className="quote-modal-action-button quote-modal-cart-button">
                    <ShoppingCart size={18} />
                    장바구니
                  </button>
                  <button onClick={handleQuoteRequest} className="quote-modal-action-button quote-modal-quote-button">
                    <Calculator size={18} />
                    견적의뢰
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* 로그인 필요 모달 */}
      {
        showLoginModal && (
          <Modal
            isOpen={showLoginModal}
            title="로그인 필요"
            message={`장바구니 및 견적의뢰를 위해서는 로그인이 필요합니다.\n로그인 페이지로 이동하시겠습니까?`}
            onConfirm={() => {
              setShowLoginModal(false);
              onClose();
              navigate('/login');
            }}
            onCancel={() => setShowLoginModal(false)}
          />
        )
      }

      {/* 장바구니 추가 성공 모달 */}
      <Modal
        isOpen={showCartSuccessModal}
        title="알림"
        message="상품이 장바구니에 담겼습니다. 장바구니로 이동하시겠습니까?"
        confirmText="장바구니 이동"
        cancelText="쇼핑 계속하기"
        onConfirm={() => {
          setShowCartSuccessModal(false);
          onClose();
          navigate('/cart');
        }}
        onCancel={() => {
          setShowCartSuccessModal(false);
        }}
      />

      {/* 견적의뢰 모달 */}
      {
        showQuoteRequestModal && (
          <ProductQuoteModal
            product={product}
            isOpen={showQuoteRequestModal}
            onClose={() => {
              setShowQuoteRequestModal(false);
            }}
            selectedProducts={[{
              itemCd: product.itemCd,
              itemNm: product.itemNm,
              optCd: product.optCd || '',
              optValCd: selectedOptionValue || '',
              optValNm: optionValues.find(opt => opt.optValCd === selectedOptionValue)?.optValNm || '',
              price: product.disPrice || product.salePrice || 0,
              quantity: quantity,
              filePath: product.FILEPATH,
              totalAmount: (product.disPrice || product.salePrice || 0) * quantity
            }]}
          />
        )
      }

      {/* 이미지 확대 모달 */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={(e) => {
          e && e.stopPropagation && e.stopPropagation();
          setIsImageModalOpen(false);
        }}
        images={images.map((url, index) => ({
          url: url,
          alt: `${product.itemNm} ${index + 1}`,
          title: product.itemNm
        }))}
        initialIndex={currentImageIndex}
        title={product.itemNm}
        showControls={true}
      />
    </>
  );
};

export default QuoteModal;
